import React, { useEffect, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { AiOutlineCheck, AiOutlineClose } from "react-icons/ai";
import swal from "sweetalert";
import AdminLayout from "../../Layout/index.jsx";
import { getAuth } from "firebase/auth";
import Pagination from "../../../../components/Pagination/index.jsx";


// GraphQL Queries & Mutations
const GET_ALL_REQUESTS = gql`
  query GetAllRequests {
    getAllRequests {
      id
      typeOfDeletion
      reasonForDeletion
      requestedBy
      title
      statusOfApproval
      requestedAt
    }
  }
`;

const APPROVE_REQUEST = gql`
  mutation ApproveRequest($id: String!, $admin: String!, $token: String!) {
    approveRequest(id: $id, admin: $admin, token: $token) {
      id
      typeOfDeletion
      statusOfApproval
      approvedBy
      requestedBy
      requestedAt
      title
      reasonForDeletion
      payload
      deleteResult {
        successCount
        failedCount
        deletedEmails
        failedStudents {
          email
          studentId
          reason
        }
        message
      }
    }
  }
`;

const REJECT_REQUEST = gql`
  mutation RejectRequest($id: String!, $admin: String!) {
    rejectRequest(id: $id, admin: $admin) {
      id
      statusOfApproval
      approvedBy
    }
  }
`;

const superAdminEmails = [
  "training@thebettertomorrow.in",
  "bettertomorrowoffi@gmail.com",
  "beta-admin@bt.in",
];

const ApprovalList = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && !superAdminEmails.includes(user.email)) {
        navigate("/admin", { replace: true });
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const { data, loading, error, refetch } = useQuery(GET_ALL_REQUESTS, {
    fetchPolicy: "network-only",
  });

  // Log all approval data with status and everything
  useEffect(() => {
    if (data && data.getAllRequests) {
     
    }
  }, [data]);
  const [approveRequest] = useMutation(APPROVE_REQUEST);
  const [rejectRequest] = useMutation(REJECT_REQUEST);

  const [filterType, setFilterType] = useState("ALL");
  const [searchText, setSearchText] = useState("");
  const [isRefetching, setIsRefetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showApproved, setShowApproved] = useState(false); // 👈 NEW toggle
  const [deletingModal, setDeletingModal] = useState({
    visible: false,
    status: "deleting",
    message: "",
    count: 0,
    showWait: false,
    approvalResult: null,
    approvalPayload: null,
  });
  const itemsPerPage = 10;
  const adminId = localStorage.getItem("userId") || "admin";

  // Get Firebase token
  const getToken = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken(true);
      localStorage.setItem("idtoken", token);
      return token;
    }
    return null;
  };

  useEffect(() => {
    const refresh = async () => {
      setIsRefetching(true);
      await refetch();
      setIsRefetching(false);
    };
    refresh();
  }, [refetch]);

  const getDeletingLabel = (type) => {
    if (type === "CLUSTER_WITH_STUDENTS") return "Deleting student profiles";
    if (type === "STUDENT") return "Deleting student profile";
    if (type === "CLUSTER_SUBMISSION") return "Clearing submissions";
    if (type === "CLUSTER") return "Removing cluster";
    if (type === "STUDENT_SUBMISSION") return "Clearing submissions";
    if (type === "BULK_STUDENT") return "Deleting student profiles";
    return "Processing";
  };

  const getDoneLabel = (type) => {
    if (type === "CLUSTER_WITH_STUDENTS") return "Student profiles deleted";
    if (type === "STUDENT") return "Student profile deleted";
    if (type === "CLUSTER_SUBMISSION") return "Submissions cleared";
    if (type === "CLUSTER") return "Cluster removed";
    if (type === "STUDENT_SUBMISSION") return "Submissions cleared";
    if (type === "BULK_STUDENT") return "Student profiles deleted";
    return "Completed";
  };

  const handleApprove = async (approval) => {
    const id = approval.id;
    const payload = typeof approval.payload === "string" ? JSON.parse(approval.payload) : approval.payload;
    let studentCount = 0;
    if (approval.typeOfDeletion === "CLUSTER_WITH_STUDENTS" && payload?.variables?.studentIds) {
      studentCount = payload.variables.studentIds.length;
    } else if (approval.typeOfDeletion === "STUDENT") {
      studentCount = 1;
    } else if (approval.typeOfDeletion === "BULK_STUDENT" && payload?.variables?.studentIds) {
      studentCount = payload.variables.studentIds.length;
    }

    const deletingMsg = getDeletingLabel(approval.typeOfDeletion);
    const showWait = ["CLUSTER_WITH_STUDENTS", "STUDENT", "BULK_STUDENT"].includes(approval.typeOfDeletion);
    setDeletingModal({
      visible: true,
      status: "deleting",
      message: deletingMsg,
      count: studentCount,
      showWait,
      approvalResult: null,
      approvalPayload: payload,
    });

    try {
      const token = await getToken();
      const result = await approveRequest({
        variables: { id, admin: adminId, token },
        context: { headers: { Authorization: `Bearer ${token}` } },
      });

      const doneMsg = getDoneLabel(approval.typeOfDeletion);
      setDeletingModal({
        visible: true,
        status: "success",
        message: doneMsg,
        count: studentCount,
        showWait: false,
        approvalResult: result?.data?.approveRequest ?? null,
        approvalPayload: payload,
      });

      refetch();
    } catch (err) {
      console.error(err);
      setDeletingModal({
        visible: true,
        status: "error",
        message: "Failed to approve request",
        count: 0,
        showWait: false,
        approvalResult: null,
        approvalPayload: payload,
      });
    }
  };

  const handleReject = async (id) => {
    try {
      const token = await getToken();
      await rejectRequest({
        variables: { id, admin: adminId },
        context: { headers: { Authorization: `Bearer ${token}` } },
      });
      swal("Rejected!", "The request has been rejected.", "info");
      refetch();
    } catch (err) {
      console.error(err);
      swal("Error", "Failed to reject request.", "error");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="space-y-3 animate-pulse">
            <div className="w-1/3 h-6 bg-gray-200 rounded"></div>
            <div className="w-2/3 h-4 bg-gray-200 rounded"></div>
            <div className="w-full h-4 bg-gray-200 rounded"></div>
            <div className="w-3/4 h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error)
    return (
      <AdminLayout>
        <div className="p-6 font-medium text-center text-red-600">
          ⚠️ Error fetching approvals
        </div>
      </AdminLayout>
    );

  // Base data
  let approvals = [...(data?.getAllRequests || [])];

  // 👇 Filter logic for "Approved Requests" view
  approvals = showApproved
    ? approvals.filter((a) => a.statusOfApproval === "APPROVED")
    : approvals.filter((a) => a.statusOfApproval !== "APPROVED");

  // Sort by newest requests first
  approvals.sort((a, b) => {
    const timeA = new Date(a.requestedAt).getTime();
    const timeB = new Date(b.requestedAt).getTime();
    return (Number.isNaN(timeB) ? 0 : timeB) - (Number.isNaN(timeA) ? 0 : timeA);
  });

  // Extract unique types for dropdown
  const uniqueTypes = Array.from(new Set(approvals.map((a) => a.typeOfDeletion))).filter(Boolean);

  // Apply type and search filters
  if (filterType !== "ALL") {
    approvals = approvals.filter((a) => a.typeOfDeletion === filterType);
  }

  if (searchText.trim()) {
    const lower = searchText.toLowerCase();
    approvals = approvals.filter(
      (a) =>
        a.title.toLowerCase().includes(lower) ||
        a.requestedBy.toLowerCase().includes(lower)
    );
  }

  // Pagination
  const totalPages = Math.ceil(approvals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedApprovals = approvals.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100 text-green-800 border-green-300";
      case "REJECTED":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
    }
  };

  const formatRequestedAt = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const summarizeBulkDeleteResult = (approvalResult, approvalPayload) => {
    const resultSources = [approvalResult?.payload, approvalResult, approvalPayload].filter(Boolean);

    for (const source of resultSources) {
      const deleteResult =
        source?.deleteBulkStudents ||
        source?.bulkDeleteResult ||
        source?.deleteResult ||
        source?.result ||
        source?.data?.deleteBulkStudents ||
        null;

      if (deleteResult) {
        return {
          successCount: deleteResult.successCount ?? deleteResult.deletedEmails?.length ?? 0,
          failedCount: deleteResult.failedCount ?? deleteResult.failedStudents?.length ?? 0,
          deletedEmails: Array.isArray(deleteResult.deletedEmails) ? deleteResult.deletedEmails : [],
          failedStudents: Array.isArray(deleteResult.failedStudents) ? deleteResult.failedStudents : [],
          message: deleteResult.message || "Bulk delete completed.",
        };
      }
    }

    return null;
  };
 
  return (
    <AdminLayout>
      <div className="p-6 transition bg-white rounded-lg shadow-md">
        {/* Header Section */}
        <div className="flex flex-col items-center justify-between gap-3 mb-6 md:flex-row">
          <h2 className="flex items-center gap-2 text-3xl font-bold text-gray-800">
            {showApproved ? "Approved Requests" : "Approval Requests"}
            {isRefetching && (
              <span className="w-3 h-3 ml-2 bg-green-400 rounded-full animate-ping"></span>
            )}
          </h2>

          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Search by title/requester..."
              className="px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
            />
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Types</option>
              {uniqueTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            {/* 👇 Toggle Buttons */}
            <button
              onClick={() => setShowApproved(false)}
              className={`px-4 py-2 rounded-lg font-semibold ${
                !showApproved
                  ? "bg-blue-600 text-white"
                  : "bg-primary-200 "
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setShowApproved(true)}
              className={`px-4 py-2 rounded-lg font-semibold ${
                showApproved
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Approved
            </button>
          </div>
        </div>

        {/* Table Section */}
        {approvals.length === 0 ? (
          <p className="py-10 text-center text-gray-500">
            {showApproved
              ? "No approved requests found."
              : "No pending or rejected requests found."}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full overflow-hidden border border-gray-200 rounded-lg table-auto">
                <thead className="bg-gray-100">
                  <tr>
                    {["Type", "Title", "Reason", "Requested By", "Requested At", "Status", "Actions"].map((head) => (
                      <th
                        key={head}
                        className="px-4 py-3 font-semibold text-left text-gray-700 border"
                      >
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedApprovals.map((approval, idx) => (
                    <tr
                      key={approval.id}
                      className={`transition-all duration-200 ${
                        idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                      } hover:bg-blue-50`}
                    >
                      <td className="px-4 py-2 border">{approval.typeOfDeletion}</td>
                      <td className="px-4 py-2 font-medium border">{approval.title}</td>
                      <td className="px-4 py-2 text-sm text-gray-600 border">
                        {approval.reasonForDeletion}
                      </td>
                      <td className="px-4 py-2 text-sm border">{approval.requestedBy}</td>
                      <td className="px-4 py-2 text-sm border whitespace-nowrap">
                        {formatRequestedAt(approval.requestedAt)}
                      </td>
                      <td className="px-4 py-2 border">
                        <span
                          className={`inline-block px-2 py-1 rounded-full border font-semibold text-xs ${getStatusColor(
                            approval.statusOfApproval
                          )}`}
                        >
                          {approval.statusOfApproval}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center border">
                        {!showApproved && (
                          <div className="flex justify-center gap-3">
                            <button
                              onClick={() => handleApprove(approval)}
                              disabled={approval.statusOfApproval !== "PENDING"}
                              className={`p-2 rounded-full border border-green-500 hover:bg-green-100 transition-all duration-200 ${
                                approval.statusOfApproval !== "PENDING"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              title="Approve Request"
                            >
                              <AiOutlineCheck className="w-5 h-5 text-green-600" />
                            </button>
                            <button
                              onClick={() => handleReject(approval.id)}
                              disabled={approval.statusOfApproval !== "PENDING"}
                              className={`p-2 rounded-full border border-red-500 hover:bg-red-100 transition-all duration-200 ${
                                approval.statusOfApproval !== "PENDING"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              title="Reject Request"
                            >
                              <AiOutlineClose className="w-5 h-5 text-red-600" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      {/* Deleting / Result Modal */}
      {deletingModal.visible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.45)",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "48px 56px 44px",
              textAlign: "center",
              boxShadow: "0 6px 32px rgba(0,0,0,0.18)",
              width: "480px",
              maxWidth: "90vw",
              minHeight: "360px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={() =>
                setDeletingModal({
                  visible: false,
                  status: "deleting",
                  message: "",
                  count: 0,
                  showWait: false,
                  approvalResult: null,
                  approvalPayload: null,
                })
              }
              aria-label="Close result modal"
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                width: 32,
                height: 32,
                borderRadius: 9999,
                border: "1px solid #e5e7eb",
                background: "#fff",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
              }}
            >
              <AiOutlineClose className="w-4 h-4" />
            </button>

            {/* Bin Icon */}
            <div style={{ margin: "0 auto 35px", width: 64, height: 64, position: "relative" }}>
  <svg
    width="64"
    height="64"
    viewBox="0 0 100 100"
                fill="none"
                style={{
                  animation:
                    deletingModal.status === "deleting"
                      ? "binShake 0.5s ease-in-out infinite"
                      : deletingModal.status === "success"
                        ? "binPop 0.4s ease-out"
                        : "none",
                }}
              >
                {/* Bin body */}
                <path
                  d="M25 35h50v45a5 5 0 0 1-5 5H30a5 5 0 0 1-5-5V35z"
                  fill="#fef2f2"
                  stroke="#dc2626"
                  strokeWidth="2.5"
                />
                {/* Lid */}
                <path
                  d="M15 25h70v6a3 3 0 0 1-3 3H18a3 3 0 0 1-3-3v-6z"
                  fill="#fee2e2"
                  stroke="#dc2626"
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                />
                {/* Lid handle */}
                <rect x="42" y="18" width="16" height="7" rx="3" fill="#dc2626" />
                {/* Lines on bin */}
                <line x1="38" y1="48" x2="38" y2="70" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="50" y1="48" x2="50" y2="70" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="62" y1="48" x2="62" y2="70" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" />

                {/* Success checkmark overlay */}
                {deletingModal.status === "success" && (
                  <g>
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#22c55e" strokeWidth="2.5" opacity="0.2" />
                    <polyline points="38 50 46 58 62 42" stroke="#22c55e" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                )}

                {/* Error X overlay */}
                {deletingModal.status === "error" && (
                  <g>
                    <circle cx="50" cy="50" r="38" fill="none" stroke="#ef4444" strokeWidth="2.5" opacity="0.2" />
                    <line x1="38" y1="38" x2="62" y2="62" stroke="#ef4444" strokeWidth="3.5" strokeLinecap="round" />
                    <line x1="62" y1="38" x2="38" y2="62" stroke="#ef4444" strokeWidth="3.5" strokeLinecap="round" />
                  </g>
                )}
              </svg>
              {deletingModal.status === "deleting" && (
                <div
                  style={{
                    position: "absolute",
                    inset: -4,
                    borderRadius: "50%",
                    border: "3px solid #fecaca",
                    borderTopColor: "#dc2626",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
              )}
            </div>

            {/* Text with 3-dot animation */}
            <p style={{ fontSize: 18, fontWeight: 600, color: "#111827", margin: 0, minHeight: 28 }}>
              {deletingModal.message}
              {deletingModal.status === "deleting" && (
                <span className="dot-anim">...</span>
              )}
            </p>
            {deletingModal.status === "success" && deletingModal.approvalResult && (() => {
              const summary = summarizeBulkDeleteResult(
                deletingModal.approvalResult,
                deletingModal.approvalPayload
              );

              if (!summary) return null;

              return (
                <div className="mt-5 w-full rounded-xl border border-green-200 bg-green-50 p-4 text-left shadow-sm">
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="rounded-lg bg-white p-3 border border-green-100">
                        <div className="text-xs font-semibold uppercase text-gray-500">Deleted</div>
                        <div className="mt-1 text-lg font-bold text-gray-800">{summary.successCount}</div>
                      </div>
                      <div className="rounded-lg bg-white p-3 border border-red-100">
                        <div className="text-xs font-semibold uppercase text-gray-500">Failed</div>
                        <div className="mt-1 text-lg font-bold text-gray-800">{summary.failedCount}</div>
                      </div>
                    </div>

                    {summary.failedStudents?.length > 0 && (
                      <div>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Failed Student Emails
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {summary.failedStudents.map((student, index) => (
                            <span
                              key={`${student.email || student.studentId || index}`}
                              className="rounded-full bg-red-100 px-2.5 py-1 text-xs text-red-700"
                            >
                              {student.email || student.studentId || "-"}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {summary.failedStudents?.length === 0 && (
                      <div className="rounded-lg border border-green-200 bg-white p-3 text-sm text-green-700">
                        No failed email IDs.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            {deletingModal.status === "deleting" && deletingModal.showWait && (
              <p style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>
                Please wait while the operation completes.
              </p>
            )}
            {deletingModal.status === "error" && (
              <div className="mt-5 w-full rounded-xl border border-red-200 bg-red-50 p-4 text-left shadow-sm">
                <div className="text-sm font-semibold text-red-700">Approval failed</div>
                <div className="mt-1 text-sm text-red-800">The request could not be approved. You can close this modal and try again.</div>
              </div>
            )}
          </div>

          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
            @keyframes binShake {
              0%, 100% { transform: rotate(0deg); }
              25% { transform: rotate(-6deg); }
              75% { transform: rotate(6deg); }
            }
            @keyframes binPop {
              0% { transform: scale(0.3); opacity: 0; }
              70% { transform: scale(1.1); }
              100% { transform: scale(1); opacity: 1; }
            }
            .dot-anim { animation: dotPulse 1.4s steps(4, end) infinite; }
            @keyframes dotPulse {
              0% { opacity: 0.2; }
              25% { opacity: 0.5; }
              50% { opacity: 0.8; }
              75% { opacity: 1; }
              100% { opacity: 0.2; }
            }
          `}</style>
        </div>
      )}
    </AdminLayout>
  );
};

export default ApprovalList;
